// React
import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from "react";

// Libraries
import PropTypes from "prop-types";
import { findIndex, isEmpty } from "lodash";
import { GroupedVirtuoso } from "react-virtuoso";
import { Button, Icon } from "semantic-ui-react";

// Components
import Text from "@Components/Text";
import Pill from "@Components/Pill";
import MessageCard from "../MessageCard";
import MessageListHeader from "./Subcomponents/Header";

// Styles
import styles from "./styles.module.css";

const MessageList = forwardRef(
	(
		{
			sectionsData,
			messagesData,
			dataCounts,
			fetchMoreMessages,
			isLoading,
			isChatDisabled,
			platformTypes,
			handleShare,
			clientName,
			clientPhone,
			platformName,
		},
		ref
	) => {
		const [atBottom, setAtBottom] = useState(false);
		const [atTop, setAtTop] = useState(false);
		const [hasMoreMessages, setHasMoreMessages] = useState(true);
		const isFetchingRef = useRef(false);
		const listRef = useRef(null);

		useEffect(() => {
			if (!atTop || !hasMoreMessages || isFetchingRef.current) {
				return;
			}
			isFetchingRef.current = true;
			fetchMoreMessages((newItemsHeight, newMessages) => {
				isFetchingRef.current = false;
				if (isEmpty(newMessages)) {
					setHasMoreMessages(false);
				} else if (listRef?.current && newItemsHeight > 0) {
					const scrollTop = listRef?.current.scrollTop || 0;
					listRef?.current.scrollTo({ top: scrollTop + newItemsHeight });
				}
			});
		}, [atTop, hasMoreMessages]);

		useImperativeHandle(ref, () => {
			return {
				scrollToBottom: handleScrollToBottom,
				scrollToIndex: handleScrollToIndex,
				scrollToMessage: handleScrollToMessage,
			};
		});

		const handleScrollToBottom = () => {
			listRef?.current?.scrollToIndex({ index: messagesData.length - 1, behavior: "smooth" });
		};

		const handleScrollToIndex = (index) => {
			listRef?.current?.scrollToIndex({ index, behavior: "smooth" });
		};

		const handleScrollToMessage = async (msgId) => {
			const messageIndex = findIndex(messagesData, { id: msgId });
			if (messageIndex >= 0) {
				listRef?.current?.scrollToIndex(messageIndex);
			} else {
				fetchMoreMessages(() => {
					if (listRef?.current) {
						listRef?.current.scrollTo({ top: 1 });
					}
				}, msgId);
			}
		};

		const renderHeader = (index) => {
			return (
				<div className={styles.datePill}>
					<Pill>
						<Text className={styles.pillText}>{sectionsData[index]}</Text>
					</Pill>
				</div>
			);
		};

		const renderItem = (index) => {
			const item = messagesData[index];
			return (
				<MessageCard
					item={item}
					isChatDisabled={isChatDisabled}
					platformTypes={platformTypes}
					handleShare={handleShare}
					clientName={clientName}
					clientPhone={clientPhone}
					platformName={platformName}
				/>
			);
		};

		return (
			<div className={styles.fullSize}>
				{messagesData?.length > 0 && dataCounts?.length > 0 && (
					<GroupedVirtuoso
						ref={listRef}
						groupCounts={dataCounts}
						context={{ isLoading }}
						components={{ Header: MessageListHeader }}
						groupContent={renderHeader}
						itemContent={renderItem}
						atBottomStateChange={setAtBottom}
						atTopStateChange={setAtTop}
						overscan={25}
						followOutput="smooth"
						initialTopMostItemIndex={messagesData.length - 1}
					/>
				)}

				{!atBottom && (
					<Button className={styles.fab} icon circular onClick={handleScrollToBottom}>
						<Icon className={styles.fabIcon} name="angle down" size="large" />
					</Button>
				)}
			</div>
		);
	}
);

MessageList.propTypes = {
	sectionsData: PropTypes.array,
	messagesData: PropTypes.array,
	dataCounts: PropTypes.array,
	fetchMoreMessages: PropTypes.func,
	handleShare: PropTypes.func,
	isLoading: PropTypes.bool,
	isChatDisabled: PropTypes.bool,
	platformTypes: PropTypes.array,
	clientName: PropTypes.string,
	clientPhone: PropTypes.string,
	platformName: PropTypes.string,
};

export default MessageList;
